import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { UserStateService } from '../services/user-state.service';
import { firstValueFrom } from 'rxjs';
import { ROLE_HIERARCHY } from '../../shared/models/role-hierarchy-model';

export const navigationHandlers = {
  redirectToPricing(url: string): void {
    window.location.assign(url);
  },
};

export const roleGuard = (requiredRole: string): CanActivateFn => {
  return async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    
    const auth = inject(AuthService);
    const router = inject(Router);
    const userState = inject(UserStateService);

    // 1. Check authenticated user via idTokenClaims$
    const claims = await firstValueFrom(auth.idTokenClaims$);

    if (!claims) {
      // Not logged in → go to login
      await auth.loginWithRedirect({ appState: { target: state.url } });
      return false;
    }

    // 2. Ensure role data is loaded on direct navigation/page refresh before checking access
    const onboardingState = await firstValueFrom(userState.getOrFetchState());

    // 3. Let the onboarding guard own redirecting users who have not completed plan selection
    if (onboardingState.accountStatus === 'new') {
      await router.navigate(['/resolving'], { state: { target: state.url } });
      return false;
    }

    // 4. Check role access
    const userRoleIndex = ROLE_HIERARCHY.indexOf(onboardingState.accountStatus);
    const requiredRoleIndex = ROLE_HIERARCHY.indexOf(requiredRole);

    if (userRoleIndex === -1 || requiredRoleIndex === -1 || userRoleIndex < requiredRoleIndex) {
      const featureName = encodeURIComponent(state.url);
      const req = encodeURIComponent(requiredRole);

      navigationHandlers.redirectToPricing(`/pricing?feature=${featureName}&required=${req}`);
      return false;
    }

    // 5. Access granted
    return true;
  };
};
